/**
 * iter698 — POS Cashier toolbar actions extracted into a configurable
 * component. The cashier-facing top bar was getting crowded (6 chips
 * fighting for space + scrolling horizontally on tablets), so this
 * component:
 *
 *   1. Splits the action chips into three tiers
 *        primary  → always visible
 *        overflow → behind a "⋯" dropdown
 *        hidden   → never rendered
 *      Each device gets its own layout via localStorage["pos_toolbar_v1"].
 *
 *   2. Applies role gating
 *        cashier  → leaner default (Orders + Drawer + Shift)
 *        manager+ → fuller default (Orders + Day Report + Shift + Drawer + Printers)
 *      Operators with a `pos_toolbar.*` permission see those chips even if
 *      their base role wouldn't.
 *
 *   3. Country gating (future-proofing)
 *      Each registry entry can declare `requiresCountry: ['SA', ...]` so
 *      e.g. ZATCA-specific chips only render in Saudi Arabia. None of the
 *      five current chips are country-locked, but the mechanism is in.
 *
 *   4. Customise dialog (admin-only) — opened from the "⚙" overflow item.
 *      Three-column UI lets the operator drag any chip between Primary /
 *      Overflow / Hidden and Save. Saves to localStorage only — zero
 *      backend changes, so it's impossible to corrupt live data.
 *
 * IMPORTANT: this is pure layout — every onClick callback is exactly
 * the same code path that lived inline in CashierPOSPage.jsx before, so
 * orders, drawer-kick, shift, day-report XML printing all behave
 * identically.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Receipt, Printer, FileText, Wallet, PlayCircle,
  MoreHorizontal, Settings as SettingsIcon, X, RefreshCcw,
  ShieldCheck, Cpu, Monitor, Cloud, Building2, ScanLine, BarChart3, Scale, Utensils,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '@/components/ui/sheet';
import {
  RadioGroup, RadioGroupItem,
} from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';

// ── Registry ────────────────────────────────────────────────────────
// Every chip the operator can toggle on/off lives here. Adding a new
// chip means adding one entry + handling the action in CashierPOSPage.
// Keep `label` short so it fits in the overflow menu too.
const ACTION_REGISTRY = (handlers) => ({
  orders: {
    id: 'orders',
    label: 'Orders',
    icon: Receipt,
    testId: 'orders-history-btn',
    onClick: handlers.onOpenOrders,
    description: 'View today\u2019s orders and reprint receipts.',
  },
  printers: {
    id: 'printers',
    label: 'Printers',
    icon: Printer,
    testId: 'printer-settings-btn',
    onClick: handlers.onOpenPrinters,
    description: 'Test, configure and reassign branch printers.',
    roleMin: 'manager',
  },
  dayReport: {
    id: 'dayReport',
    label: 'Day Report',
    icon: FileText,
    testId: 'day-report-btn',
    accent: 'emerald',
    onClick: () => handlers.onPrintDayReport('today'),
    description: 'Print Z-Report / 7-day / 30-day sales summary.',
    roleMin: 'manager',
    // Dropdown lives in the registry so it survives the move into overflow.
    submenu: [
      { label: 'Today (Z-Report)',  onClick: () => handlers.onPrintDayReport('today'),  testId: 'day-report-today'  },
      { label: 'Last 7 days',       onClick: () => handlers.onPrintDayReport('week'),   testId: 'day-report-week'   },
      { label: 'Last 30 days',      onClick: () => handlers.onPrintDayReport('month'),  testId: 'day-report-month'  },
    ],
  },
  daySummary: {
    id: 'daySummary',
    label: 'Day Summary',
    icon: BarChart3,
    testId: 'day-summary-btn',
    accent: 'indigo',
    onClick: handlers.onOpenDaySummary,
    description: 'View items sold (qty & total) and cash/bank totals on screen — any time.',
  },
  openChecks: {
    id: 'openChecks',
    label: handlers.openChecksCount ? `Open Checks (${handlers.openChecksCount})` : 'Open Checks',
    icon: Utensils,
    testId: 'open-checks-btn',
    accent: 'orange',
    onClick: handlers.onOpenOpenChecks,
    description: 'See active waiter/table checks for this branch and take payment on the same order.',
    // Universal POS: only businesses whose industry has tables (restaurant/F&B)
    // expose waiter/table Open Checks. Driven by POS_INDUSTRIES.has_tables.
    requiresCapability: 'tables',
  },
  reconcile: {
    id: 'reconcile',
    label: 'Reconcile Sales',
    icon: Scale,
    testId: 'reconcile-sales-btn',
    accent: 'sky',
    onClick: handlers.onOpenReconcile,
    description: 'Controlled Sales Reconciliation — match recorded vs actual Cash/Bank/Credit.',
    // Permission-gated (not role-name): only shown when the signed-in user has
    // sales write permission. Backend require_permission stays authoritative.
    requiresCapability: 'reconcile',
  },
  drawer: {
    id: 'drawer',
    label: 'Drawer',
    icon: Wallet,
    testId: 'drawer-kick-btn',
    accent: 'amber',
    onClick: handlers.onOpenDrawer,
    description: 'Open the cash drawer (logged for audit).',
  },
  handover: {
    id: 'handover',
    label: 'Hand over cash',
    icon: PlayCircle,
    testId: 'handover-btn',
    onClick: handlers.onOpenShiftModal,  // reuses the shift dialog's Cash Handover box (shown only when the company switch is on)
    description: 'Hand drawer cash to a person who holds it for the branch.',
  },
  shift: {
    id: 'shift',
    label: 'Shift',
    icon: PlayCircle,
    testId: 'shift-btn',
    // The shift chip is "live" — colour depends on whether a shift is open.
    onClick: handlers.onOpenShiftModal,
    description: 'Open / close cashier shift with starting & ending float.',
    dynamic: (ctx) => ({
      label: ctx.currentShift ? 'Shift Active' : 'Start Shift',
      accent: ctx.currentShift ? 'success' : null,
      variant: ctx.currentShift ? 'default' : 'outline',
    }),
  },
  // iter699 — Country-gated compliance chips. KSA tenants get ZATCA &
  // Fiscal Device chips; the rest of the world doesn't see them in the
  // registry at all, keeping their toolbar lighter.
  zatca: {
    id: 'zatca',
    label: 'ZATCA',
    icon: ShieldCheck,
    testId: 'zatca-status-btn',
    accent: 'sky',
    onClick: handlers.onOpenZatcaStatus,
    description: 'View ZATCA Phase 2 e-invoicing status, cleared invoices, and CSR / certificate health.',
    roleMin: 'manager',
    requiresCountry: ['SA'],
  },
  fiscal: {
    id: 'fiscal',
    label: 'Fiscal Device',
    icon: Cpu,
    testId: 'fiscal-status-btn',
    accent: 'purple',
    onClick: handlers.onOpenFiscalStatus,
    description: 'Fiscal printer / signing device heartbeat. Required by SDC-bound jurisdictions.',
    roleMin: 'manager',
    requiresCountry: ['SA', 'IT', 'GR', 'CZ', 'HR', 'AT', 'RO', 'PL'],
  },
  customerDisplay: {
    id: 'customerDisplay',
    label: 'Customer Display',
    icon: Monitor,
    testId: 'customer-display-btn',
    accent: 'indigo',
    onClick: handlers.onOpenCustomerDisplay,
    description: 'Pop out the customer-facing screen so the buyer can see the running cart total.',
  },
});

// Role hierarchy used by role-gating.
const ROLE_RANK = { cashier: 1, manager: 2, admin: 3, super_admin: 4, owner: 4 };
const roleAtLeast = (userRole, minRole) =>
  (ROLE_RANK[userRole] || 0) >= (ROLE_RANK[minRole] || 0);

// Per-role + per-country defaults. Operators can change these any time
// via the Customise dialog; this is just the starting layout for a new
// device.
const DEFAULTS = {
  cashier: { primary: ['orders', 'openChecks', 'daySummary'],              overflow: ['drawer', 'shift', 'handover', 'reconcile'],                 hidden: ['printers', 'dayReport', 'zatca', 'fiscal', 'customerDisplay'] },
  manager: { primary: ['orders', 'openChecks', 'daySummary', 'dayReport'], overflow: ['drawer', 'shift', 'handover', 'printers', 'reconcile'],      hidden: ['zatca', 'fiscal', 'customerDisplay'] },
  admin:   { primary: ['orders', 'openChecks', 'daySummary', 'dayReport', 'shift'], overflow: ['drawer', 'handover', 'printers', 'reconcile', 'zatca', 'fiscal'],  hidden: ['customerDisplay'] },
};

const STORAGE_KEY = 'pos_toolbar_v1';

// Every action id in the registry — used to reconcile saved configs so
// newly-added actions (e.g. daySummary) surface on existing devices.
const ALL_ACTION_IDS = ['orders', 'printers', 'dayReport', 'daySummary', 'openChecks', 'reconcile', 'drawer', 'shift', 'handover', 'zatca', 'fiscal', 'customerDisplay'];

function reconcile(cfg) {
  const placed = new Set([...(cfg.primary || []), ...(cfg.overflow || []), ...(cfg.hidden || [])]);
  const missing = ALL_ACTION_IDS.filter((id) => !placed.has(id));
  if (!missing.length) return cfg;
  // New headline features go straight to the primary row so they're
  // discoverable on existing devices; everything else lands in overflow.
  const promote = new Set(['daySummary']);
  const toPrimary = missing.filter((id) => promote.has(id));
  const toOverflow = missing.filter((id) => !promote.has(id));
  return {
    ...cfg,
    primary: [...(cfg.primary || []), ...toPrimary],
    overflow: [...(cfg.overflow || []), ...toOverflow],
  };
}

function loadConfig(role) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const cfg = JSON.parse(raw);
      if (cfg && Array.isArray(cfg.primary)) return surfaceReconcile(promoteDaySummary(promoteOpenChecks(reconcile(cfg))), 'pos_toolbar_reconcile_surfaced_v1');
    }
  } catch { /* ignored — corrupt storage just falls back to default */ }
  return DEFAULTS[role] || DEFAULTS.cashier;
}

// One-time migration: surface "Open Checks" on the primary toolbar for
// existing devices whose saved layout still has it buried in overflow.
// Respects later user customisation (runs only once per device).
function promoteOpenChecks(cfg) {
  try {
    if (localStorage.getItem('pos_toolbar_openchecks_promoted_v1') === '1') return cfg;
    localStorage.setItem('pos_toolbar_openchecks_promoted_v1', '1');
  } catch { return cfg; }
  if ((cfg.primary || []).includes('openChecks')) return cfg;
  const next = {
    ...cfg,
    primary: [...(cfg.primary || []).filter((id) => id !== 'openChecks')],
    overflow: (cfg.overflow || []).filter((id) => id !== 'openChecks'),
    hidden: (cfg.hidden || []).filter((id) => id !== 'openChecks'),
  };
  // Insert right after "orders" so it sits beside the order-history button.
  const at = next.primary.indexOf('orders');
  next.primary.splice(at >= 0 ? at + 1 : next.primary.length, 0, 'openChecks');
  saveConfig(next);
  return next;
}

// One-time migration: older saved layouts hid "Reconcile Sales" for cashiers; surface it in More.
// Visibility is still permission-gated via capabilities.reconcile (sales read).
function surfaceReconcile(cfg, flag) {
  try {
    if (localStorage.getItem(flag) === '1') return cfg;
    localStorage.setItem(flag, '1');
  } catch { return cfg; }
  if (!(cfg.hidden || []).includes('reconcile')) return cfg;
  return { ...cfg, hidden: cfg.hidden.filter((id) => id !== 'reconcile'), overflow: [...(cfg.overflow || []), 'reconcile'] };
}

function saveConfig(cfg) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg)); } catch { /* quota or private mode */ }
}

// One-time migration (mirrors promoteOpenChecks): ensure "Day Summary" sits
// in the Primary row for existing devices whose saved layout buried it in
// overflow/hidden, so every branch shows it consistently by default. Runs
// once per device, so an intentional later move (manager customises a branch)
// is respected.
function promoteDaySummary(cfg) {
  try {
    if (localStorage.getItem('pos_toolbar_daysummary_promoted_v1') === '1') return cfg;
    localStorage.setItem('pos_toolbar_daysummary_promoted_v1', '1');
  } catch { return cfg; }
  if ((cfg.primary || []).includes('daySummary')) return cfg;
  const next = {
    ...cfg,
    primary: [...(cfg.primary || []).filter((id) => id !== 'daySummary')],
    overflow: (cfg.overflow || []).filter((id) => id !== 'daySummary'),
    hidden: (cfg.hidden || []).filter((id) => id !== 'daySummary'),
  };
  // Place it right after "orders" for a predictable, consistent position.
  const at = next.primary.indexOf('orders');
  next.primary.splice(at >= 0 ? at + 1 : next.primary.length, 0, 'daySummary');
  saveConfig(next);
  return next;
}

// ── Chip renderers ───────────────────────────────────────────────────
function ChipButton({ action, ctx }) {
  const Icon = action.icon;
  const dyn = typeof action.dynamic === 'function' ? action.dynamic(ctx) : {};
  const label = dyn.label || action.label;
  const accent = dyn.accent || action.accent;
  const variant = dyn.variant || 'outline';
  // Accent → Tailwind colour pair (kept inline so each chip is self-contained).
  const accentCls = {
    emerald: 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800',
    amber:   'border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700',
    sky:     'border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-800',
    purple:  'border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-800',
    indigo:  'border-indigo-300 bg-indigo-50 hover:bg-indigo-100 text-indigo-800',
    success: 'bg-success hover:bg-success text-white border-transparent',
  }[accent] || 'border-border text-muted-foreground';

  if (action.submenu) {
    return (
      <div className="relative group" data-testid={`${action.testId}-wrap`}>
        <Button size="sm" variant={variant} className={`h-10 rounded-xl ${accentCls}`}
          onClick={action.onClick} data-testid={action.testId} title={action.description}>
          <Icon size={15} className="mr-1.5" />{label}
        </Button>
        <div className="absolute right-0 top-full mt-1 z-20 hidden group-hover:block bg-white border border-emerald-200 rounded-xl shadow-lg overflow-hidden min-w-[160px]">
          {action.submenu.map((sub, i) => (
            <button
              key={sub.testId || i}
              type="button"
              onClick={sub.onClick}
              className={`w-full text-left px-3 py-2 text-[12px] hover:bg-emerald-50 ${i > 0 ? 'border-t border-emerald-100' : ''}`}
              data-testid={sub.testId}
            >
              {sub.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Button size="sm" variant={variant} className={`h-10 rounded-xl ${accentCls}`}
      onClick={action.onClick} data-testid={action.testId} title={action.description}>
      <Icon size={15} className="mr-1.5" />{label}
    </Button>
  );
}

// ── Bottom-sheet tile (iter956) ──────────────────────────────────────
function ToolTile({ icon: Icon, label, accent, testId, onClick, subtle = false }) {
  const accentCls = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    amber:   'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
    sky:     'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100',
    purple:  'border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100',
    indigo:  'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
  }[accent] || 'border-border bg-card text-foreground hover:bg-muted/60';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex flex-col items-center justify-center gap-2 rounded-2xl border p-4 min-h-[92px] transition-all active:scale-95 ${accentCls} ${subtle ? 'opacity-80' : ''}`}
    >
      <span className="rounded-xl bg-white/60 p-2.5 shadow-sm">
        {Icon ? <Icon size={22} strokeWidth={2} /> : null}
      </span>
      <span className="text-[11px] font-semibold text-center leading-tight">{label}</span>
    </button>
  );
}

// ── Main toolbar ─────────────────────────────────────────────────────
export default function POSToolbarActions({
  userRole = 'cashier',
  countryCode = '',
  currentShift = null,
  branchId = null,
  capabilities = {},
  // Handlers — each one is the same function CashierPOSPage was calling inline.
  onOpenOrders,
  onOpenPrinters,
  onOpenDrawer,
  onOpenShiftModal,
  onPrintDayReport,
  onOpenDaySummary,
  onOpenReconcile,
  onOpenOpenChecks,
  openChecksCount = 0,
  onOpenZatcaStatus,
  onOpenFiscalStatus,
  onOpenCustomerDisplay,
  onScanBill,
  onChangeOrderType,
}) {
  const [config, setConfig] = useState(() => loadConfig(userRole));
  const [showSettings, setShowSettings] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);  // iter956 — bottom-sheet "More Tools"
  // iter699 — Remote scope tracking. `remote` is what the backend last
  // told us (branch / tenant / default). The dialog's Save button uses
  // it to decide whether the operator is editing a branch-specific or
  // tenant-wide layout.
  const [remote, setRemote] = useState({ scope: 'default', branch_id: null, config: null });

  // iter699 — Fetch the saved layout on mount AND whenever the branch
  // changes (e.g. a manager shift-swaps to another till). Backend
  // resolution: branch > tenant > none. Fall back to localStorage /
  // role-default if the request fails or returns nothing.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/pos/toolbar-config', {
          params: branchId ? { branch_id: branchId } : {},
        });
        if (cancelled) return;
        setRemote(data);
        if (data?.config && Array.isArray(data.config.primary)) {
          setConfig(surfaceReconcile(reconcile(data.config), `pos_toolbar_reconcile_surfaced_remote_v1_${branchId || 'all'}`));
        } else if (!localStorage.getItem(STORAGE_KEY)) {
          setConfig(DEFAULTS[userRole] || DEFAULTS.cashier);
        }
      } catch {
        // Offline / 401 from cashier token? Just keep using localStorage.
      }
    })();
    return () => { cancelled = true; };
  }, [branchId, userRole]);

  const registry = useMemo(() => ACTION_REGISTRY({
    onOpenOrders, onOpenPrinters, onOpenDrawer, onOpenShiftModal, onPrintDayReport, onOpenDaySummary,
    onOpenReconcile, onOpenZatcaStatus, onOpenFiscalStatus, onOpenCustomerDisplay, onScanBill, onOpenOpenChecks, openChecksCount,
  }), [onOpenOrders, onOpenPrinters, onOpenDrawer, onOpenShiftModal, onPrintDayReport, onOpenDaySummary,
       onOpenReconcile, onOpenZatcaStatus, onOpenFiscalStatus, onOpenCustomerDisplay, onScanBill, onOpenOpenChecks, openChecksCount]);

  const ctx = { currentShift, countryCode, userRole };
  const [custodyOn, setCustodyOn] = useState(false);
  useEffect(() => {
    const tk = localStorage.getItem('cashier_token');
    api.get('/cash-custody/enabled', tk ? { headers: { Authorization: `Bearer ${tk}` } } : undefined)
      .then(({ data }) => setCustodyOn(!!data?.enabled)).catch(() => setCustodyOn(false));
  }, []);

  // Filter helper: respects role-min + country + permission capability.
  const allowed = (id) => {
    const a = registry[id];
    if (!a) return false;
    if (id === 'handover' && !custodyOn) return false;  // Cash Handover hidden while the company switch is OFF
    if (a.roleMin && !roleAtLeast(userRole, a.roleMin)) return false;
    if (a.requiresCountry && !a.requiresCountry.includes(countryCode)) return false;
    if (a.requiresCapability && !capabilities[a.requiresCapability]) return false;
    return true;
  };

  // Shift (open/close + Day-End Difference) is pinned first on the toolbar so tablets reach it in one tap.
  const pinned = config.overflow.includes('shift') || config.primary.includes('shift') ? ['shift'] : [];
  const primaryIds  = [...pinned, ...config.primary.filter((id) => id !== 'shift')].filter(allowed);
  const overflowIds = config.overflow.filter((id) => id !== 'shift').filter(allowed);
  const isAdmin = roleAtLeast(userRole, 'manager');

  return (
    <>
      {/* Primary tier — sit inline in the toolbar. */}
      {primaryIds.map((id) => (
        <ChipButton key={id} action={registry[id]} ctx={ctx} />
      ))}

      {/* Overflow tier — iter956: redesigned as a touch-friendly bottom-sheet
          grid of icon tiles (replaces the cramped dropdown list). */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <Button size="sm" variant="outline" className="h-10 rounded-xl border-border text-muted-foreground" data-testid="pos-toolbar-overflow-btn" title="More tools">
            <MoreHorizontal size={16} />
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="rounded-t-2xl" data-testid="pos-toolbar-overflow-sheet">
          <SheetHeader className="mb-3">
            <SheetTitle className="text-base">More Tools</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 pb-4">
            {onScanBill && (
              <ToolTile
                icon={ScanLine}
                label="Scan Bill"
                accent="emerald"
                testId="pos-scan-bill-btn"
                onClick={() => { setSheetOpen(false); onScanBill(); }}
              />
            )}
            {overflowIds.map((id) => {
              const a = registry[id];
              const dyn = typeof a.dynamic === 'function' ? a.dynamic(ctx) : {};
              const label = dyn.label || a.label;
              const accent = dyn.accent || a.accent;
              return (
                <ToolTile
                  key={id}
                  icon={a.icon}
                  label={label}
                  accent={accent}
                  testId={`overflow-${a.testId}`}
                  onClick={() => { setSheetOpen(false); a.onClick && a.onClick(); }}
                />
              );
            })}
            {/* Submenu entries (e.g. Day Report → 7/30 days) become their own tiles */}
            {overflowIds.flatMap((id) => {
              const a = registry[id];
              if (!a.submenu) return [];
              return a.submenu.slice(1).map((sub, i) => (
                <ToolTile
                  key={`${id}-sub-${i}`}
                  icon={a.icon}
                  label={sub.label}
                  accent={a.accent}
                  subtle
                  testId={sub.testId}
                  onClick={() => { setSheetOpen(false); sub.onClick && sub.onClick(); }}
                />
              ));
            })}
            {isAdmin && onChangeOrderType && (
              <ToolTile
                icon={RefreshCcw}
                label="Change Order Type"
                accent="indigo"
                testId="pos-change-order-type-btn"
                onClick={() => { setSheetOpen(false); onChangeOrderType(); }}
              />
            )}
            {isAdmin && (
              <ToolTile
                icon={SettingsIcon}
                label="Customise"
                testId="pos-toolbar-customise-btn"
                onClick={() => { setSheetOpen(false); setShowSettings(true); }}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Customise dialog */}
      <POSToolbarSettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        registry={registry}
        userRole={userRole}
        countryCode={countryCode}
        capabilities={capabilities}
        branchId={branchId}
        config={config}
        remote={remote}
        onChange={(next) => { setConfig(next); saveConfig(next); }}
        onRemoteUpdated={setRemote}
      />
    </>
  );
}

// Inner column renderer for the 3-bucket layout. Kept at module scope
// so React doesn't tear down the bucket on every parent re-render.
function ToolbarSettingsColumn({ title, bucket, hint, buckets, registry, move }) {
  return (
    <div className="space-y-2" data-testid={`pos-toolbar-col-${bucket}`}>
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
        {hint && <p className="text-[10px] text-muted-foreground/80 mt-0.5">{hint}</p>}
      </div>
      <div className="space-y-1.5 min-h-[120px] rounded-lg border border-dashed border-border bg-muted/20 p-2">
        {buckets[bucket].length === 0 && (
          <p className="text-[11px] text-muted-foreground/70 italic text-center py-3">Empty</p>
        )}
        {buckets[bucket].map((id) => {
          const a = registry[id];
          if (!a) return null;
          const Icon = a.icon;
          return (
            <div key={id} className="flex items-center gap-2 rounded-md bg-white border border-border px-2 py-1.5 group" data-testid={`pos-toolbar-chip-${id}`}>
              <Icon size={13} className="text-muted-foreground shrink-0" />
              <span className="text-xs font-medium flex-1 truncate">{a.label}</span>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {bucket !== 'primary'  && <button onClick={() => move(id, 'primary')}  className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20" data-testid={`move-${id}-primary`}>P</button>}
                {bucket !== 'overflow' && <button onClick={() => move(id, 'overflow')} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 hover:bg-zinc-200" data-testid={`move-${id}-overflow`}>O</button>}
                {bucket !== 'hidden'   && <button onClick={() => move(id, 'hidden')}   className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 hover:bg-rose-100" data-testid={`move-${id}-hidden`}>H</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Settings dialog ─────────────────────────────────────────────────
function POSToolbarSettingsDialog({ open, onOpenChange, registry, userRole, countryCode, capabilities = {}, branchId, config, remote, onChange, onRemoteUpdated }) {
  // Local draft so the operator can cancel changes.
  const [draft, setDraft] = useState(config);
  // iter699 — scope picker. Defaults to "device" (localStorage) so the
  // existing per-device behaviour is preserved unless the operator
  // actively chooses to push to all tablets at a branch / tenant-wide.
  const [scope, setScope] = useState('device');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(config); }, [config, open]);
  useEffect(() => {
    if (open) {
      // Pre-pick the scope that matches the layout we're currently
      // showing, so the picker doesn't surprise the operator.
      setScope(remote?.scope === 'branch' ? 'branch' : remote?.scope === 'tenant' ? 'tenant' : 'device');
    }
  }, [open, remote?.scope]);

  const move = (id, toBucket) => {
    setDraft((d) => {
      const next = {
        primary:  d.primary.filter((x) => x !== id),
        overflow: d.overflow.filter((x) => x !== id),
        hidden:   (d.hidden || []).filter((x) => x !== id),
      };
      next[toBucket] = [...next[toBucket], id];
      return next;
    });
  };

  const resetToDefault = () => {
    setDraft(DEFAULTS[userRole] || DEFAULTS.cashier);
  };

  // Which chips are even usable for this user (role + country gates)?
  const usable = Object.values(registry).filter((a) => {
    if (a.roleMin && !roleAtLeast(userRole, a.roleMin)) return false;
    if (a.requiresCountry && !a.requiresCountry.includes(countryCode)) return false;
    if (a.requiresCapability && !capabilities[a.requiresCapability]) return false;
    return true;
  }).map((a) => a.id);

  // Every usable id must be in exactly one bucket. Auto-place any new
  // chip that the operator hasn't seen yet (because it was added in a
  // later release) into Overflow so it surfaces without breaking the
  // primary layout.
  const buckets = ['primary', 'overflow', 'hidden'].reduce((acc, b) => ({ ...acc, [b]: (draft[b] || []).filter((x) => usable.includes(x)) }), {});
  const orphans = usable.filter((id) => !buckets.primary.includes(id) && !buckets.overflow.includes(id) && !buckets.hidden.includes(id));
  buckets.overflow = [...buckets.overflow, ...orphans];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="pos-toolbar-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SettingsIcon size={18} className="text-primary" />
            Customise POS toolbar
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Choose which action chips appear on the toolbar of this device. Saved locally — does not affect other tablets, transactions, or reports.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ToolbarSettingsColumn title="Primary"  bucket="primary"  hint="Always visible on the toolbar." buckets={buckets} registry={registry} move={move} />
          <ToolbarSettingsColumn title="Overflow" bucket="overflow" hint="Tucked behind the ⋯ menu."     buckets={buckets} registry={registry} move={move} />
          <ToolbarSettingsColumn title="Hidden"   bucket="hidden"   hint="Not shown anywhere."           buckets={buckets} registry={registry} move={move} />
        </div>

        {/* iter699 — Save scope picker. Lets an admin push the layout
            beyond this device to the whole branch or every tablet in
            the tenant in a single click. */}
        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2" data-testid="pos-toolbar-scope-picker">
          <p className="text-xs font-semibold flex items-center gap-1.5">
            <Cloud size={13} className="text-muted-foreground" /> Save layout to…
          </p>
          <RadioGroup value={scope} onValueChange={setScope} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="flex items-start gap-2 rounded-md border border-border bg-white p-2">
              <RadioGroupItem value="device" id="scope-device" className="mt-0.5" data-testid="scope-device" />
              <Label htmlFor="scope-device" className="text-[11px] leading-tight cursor-pointer flex-1">
                <span className="font-semibold">This device only</span>
                <span className="block text-muted-foreground">Just this tablet. Saved locally.</span>
              </Label>
            </div>
            <div className="flex items-start gap-2 rounded-md border border-border bg-white p-2">
              <RadioGroupItem value="branch" id="scope-branch" className="mt-0.5" disabled={!branchId} data-testid="scope-branch" />
              <Label htmlFor="scope-branch" className={`text-[11px] leading-tight flex-1 ${branchId ? 'cursor-pointer' : 'opacity-60'}`}>
                <span className="font-semibold flex items-center gap-1"><Building2 size={11} /> This branch</span>
                <span className="block text-muted-foreground">Every tablet at this branch.</span>
              </Label>
            </div>
            <div className="flex items-start gap-2 rounded-md border border-border bg-white p-2">
              <RadioGroupItem value="tenant" id="scope-tenant" className="mt-0.5" data-testid="scope-tenant" />
              <Label htmlFor="scope-tenant" className="text-[11px] leading-tight cursor-pointer flex-1">
                <span className="font-semibold flex items-center gap-1"><Cloud size={11} /> All branches</span>
                <span className="block text-muted-foreground">Default for every tablet in the business.</span>
              </Label>
            </div>
          </RadioGroup>
          {remote?.updated_at && remote?.scope !== 'default' && (
            <p className="text-[10px] text-muted-foreground/80 pt-0.5">
              Last synced from server: <strong>{remote.scope}-wide</strong>{remote.updated_by ? ` by ${remote.updated_by}` : ''} · {new Date(remote.updated_at).toLocaleString()}
            </p>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground/80">
          Tip: hover a chip and tap <strong>P</strong>/<strong>O</strong>/<strong>H</strong> to move it between Primary, Overflow, and Hidden.
          Role &amp; country restrictions still apply — chips you can&apos;t use are hidden from this list.
        </p>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={resetToDefault} data-testid="pos-toolbar-reset-btn">
            <RefreshCcw size={13} className="mr-1.5" /> Reset to {userRole} default
          </Button>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="pos-toolbar-cancel-btn">
            <X size={13} className="mr-1.5" /> Cancel
          </Button>
          <Button
            size="sm"
            className="bg-primary hover:bg-primary/90 text-white"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                // Always update local state so the toolbar reflects the
                // chosen layout immediately.
                onChange(buckets);
                if (scope !== 'device') {
                  const res = await api.post('/pos/toolbar-config', {
                    branch_id: scope === 'branch' ? branchId : null,
                    config: buckets,
                  });
                  if (res?.data?.ok) {
                    if (onRemoteUpdated) onRemoteUpdated({
                      scope: scope, branch_id: scope === 'branch' ? branchId : null,
                      config: buckets, updated_at: new Date().toISOString(),
                    });
                    toast.success(scope === 'branch'
                      ? 'Layout saved for this branch — every tablet here picks it up on next reload.'
                      : 'Layout saved tenant-wide — every tablet picks it up on next reload.');
                  }
                } else {
                  toast.success('Layout saved on this device.');
                }
                onOpenChange(false);
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Failed to save layout. Saved locally instead.');
              } finally {
                setSaving(false);
              }
            }}
            data-testid="pos-toolbar-save-btn"
          >
            {saving ? 'Saving…' : (scope === 'device' ? 'Save (this device)' : scope === 'branch' ? 'Save to this branch' : 'Save to all branches')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
